/** `apps/api` 클라이언트. 경로는 shared 의 `jobEndpoints` 에서만 가져온다.
 *  화면 문구는 status·code 로 고른다 — 서버 메시지를 그대로 쓰지 않는다. */

import {
  ANONYMOUS_SESSION,
  IDENTITY_PROVIDER_LABEL,
  UPLOAD_FILE_FIELD,
  UPLOAD_SORT_ORDER_FIELD,
  authEndpoints,
  jobEndpoints,
  paymentEndpoints,
  type ApiErrorBody,
  type ApproveJobResponse,
  type ConfirmPaymentRequest,
  type ConfirmPaymentResult,
  type CreateJobResponse,
  type ExportJobResponse,
  type IdentityProvider,
  type JobBillingView,
  type JobEventListView,
  type JobListView,
  type JobResponse,
  type JobSearchFilter,
  type JobVersionListView,
  type PaymentCheckoutView,
  type PaymentClientErrorRequest,
  type PaymentHistoryQuery,
  type PaymentHistoryView,
  type ProductCategoryStatement,
  type ProductCategoryStatementRequest,
  type ProductCategoryStatementResponse,
  type PublishSelectionRequest,
  type RefundStatementView,
  type ReorderJobImagesRequest,
  type SessionView,
  type UpdateJobBlocksRequest,
  type UpdateJobRequest,
  type UploadImageResponse,
} from "@makit/shared";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, code?: string) {
    super(`API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** 빈 값이면 같은 오리진 — dev 는 vite proxy 가 받아 CORS 를 피한다. */
export function getBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}

export async function readErrorCode(res: Response): Promise<string | undefined> {
  try {
    return ((await res.json()) as ApiErrorBody).code;
  } catch {
    return undefined;
  }
}

/** 응답을 그대로 돌려주는 요청. **401 이면 세션 게이트를 깨운다**(`notifyUnauthorized`). 실패 상태를
 *  던지지 않으므로, 실패 본문이 결과인 라우트(원본 지우기의 500 · `originals-api.ts`)가 쓴다. 응답을
 *  못 받았으면 `ApiError(0)` 을 던진다. 그 밖의 호출은 아래 `request` 를 쓴다. */
export async function send(path: string, init: RequestInit = {}): Promise<Response> {
  let res: Response;
  try {
    // 세션은 HttpOnly 쿠키에 있다. **없으면 배포본에서 모든 요청이 401 이 된다** — dev 는
    // vite 프록시가 같은 오리진으로 가려서 이 부재가 드러나지 않는다(CORS 와 같은 함정).
    res = await fetch(`${getBaseUrl()}${path}`, { credentials: "include", ...init });
  } catch {
    // 네트워크가 끊긴 경우다. 상태 코드가 없으므로 0 으로 둔다.
    throw new ApiError(0);
  }
  if (!res.ok) notifyUnauthorized(res.status, path);
  return res;
}

/** JSON 요청. 실패 상태는 `ApiError(status, code)` 로 던진다. 401 은 `send` 가 세션 게이트를 깨운다. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await send(path, init);
  if (!res.ok) throw new ApiError(res.status, await readErrorCode(res));
  return (await res.json()) as T;
}

export function createJob(): Promise<CreateJobResponse> {
  return request<CreateJobResponse>(jobEndpoints.create(), { method: "POST" });
}

/** 내 작업 목록. **찾기 조건까지 서버로 보낸다** — 조건을 클라이언트에서 거르면 두 번째
 * 페이지부터 조건이 안 걸린 행이 섞인다.
 *
 * **질의 문자열은 `jobEndpoints.list` 가 만든다.** 여기서 조립하고 있었는데, 그러면 앱이
 * 붙을 때 같은 조립이 한 번 더 생기고 두 조립이 갈리는 순간 같은 조건이 앱과 웹에서 다른
 * 목록을 낸다.
 *
 * `dateRange` 를 비워 두는 것이 맞다 — 기간 경계는 **셀러 기기의 자정** 기준이고 그것이
 * 기본값이다. 그래서 서버로 가는 값은 이미 시각까지 자른 UTC 이고, 서버가 시간대를 몰라도
 * 된다(`tzOffset` 은 날짜만 보내는 경로를 위한 서버 쪽 대비값이다). */
export function listJobs(
  filter: JobSearchFilter,
  page: { limit?: number; offset?: number } = {},
): Promise<JobListView> {
  return request<JobListView>(jobEndpoints.list({ filter, ...page }));
}

export interface UploadImageInput {
  jobId: string;
  file: File;
  /** 셀러가 정한 순서. 안 보내면 서버가 도착 순서로 매겨 대표컷이 바뀐다. */
  sortOrder: number;
  /** 0~1. 큐 보조줄의 `올리는 중 · 62%` 근거다(PRD `7.6.6` · `7.5.9`).
   *
   * ⚠️ **브라우저에서는 사실상 늘 불리지만 그것을 계약으로 삼지 않는다.** 한 번도 안 불린
   * 상태를 `0` 으로 적으면 안 된다 — 앱에서 실제로 터진 자리다(`docs/UPLOAD_PROGRESS.md`). */
  onProgress?: (ratio: number) => void;
  signal?: AbortSignal;
}

const UPLOAD_TIMEOUT_MS = 120_000;

/** `fetch` 에는 업로드 진행률 이벤트가 없어 XHR 을 쓴다. */
export function uploadImage(input: UploadImageInput): Promise<UploadImageResponse> {
  const form = new FormData();
  form.append(UPLOAD_FILE_FIELD, input.file, input.file.name);
  form.append(UPLOAD_SORT_ORDER_FIELD, String(input.sortOrder));

  return new Promise<UploadImageResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${getBaseUrl()}${jobEndpoints.uploadImage(input.jobId)}`);
    // `fetch` 쪽의 `credentials: "include"` 와 같은 뜻이다. 한쪽만 켜면 업로드만 401 이 된다.
    xhr.withCredentials = true;

    if (input.onProgress) {
      const report = input.onProgress;
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) report(event.loaded / event.total);
      };
    }

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        // `request()` 와 같은 자리다. 업로드만 빠지면 **셀러가 사진을 올리는 동안에만
        // 로그인 만료를 모르는** 상태가 되고, 그게 하필 가장 오래 머무는 화면이다.
        notifyUnauthorized(xhr.status, jobEndpoints.uploadImage(input.jobId));
        let code: string | undefined;
        try {
          code = (JSON.parse(xhr.responseText) as ApiErrorBody).code;
        } catch {
          code = undefined;
        }
        reject(new ApiError(xhr.status, code));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as UploadImageResponse);
      } catch {
        reject(new ApiError(xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError(0));
    xhr.onabort = () => reject(new ApiError(0));
    xhr.ontimeout = () => reject(new ApiError(0));
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    if (input.signal) {
      if (input.signal.aborted) {
        reject(new ApiError(0));
        return;
      }
      input.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

/** `GET /api/payments` — `W-70` 청구 내역. **달로 묶여서 온다.**
 *
 * ⚠️ **질의 문자열을 여기서 조립하지 않는다.** `paymentEndpoints.history` 가 만들고, 그
 * 함수가 **브라우저 시간대를 언제나 실어 보낸다** — 빼면 서버가 UTC 로 묶어 월말 밤 줄이
 * 지난 달 머리글 밑으로 들어간다. 앱이 같은 함수를 부르므로 두 화면의 달 경계가 갈릴 수 없다.
 *
 * **화면이 곱하지 않는다.** 줄 금액도 월 합계도 서버가 셈한 값이다(PRD 7.7.8). */
export function fetchPaymentHistory(query?: PaymentHistoryQuery): Promise<PaymentHistoryView> {
  return request<PaymentHistoryView>(paymentEndpoints.history(query));
}

/** `GET /api/payments/:paymentId` — 행 아래에서 제자리로 펼치는 정산 명세.
 *
 * **`jobId` 가 아니라 `paymentId` 다.** 한 작업에 결제가 여럿 남을 수 있어(실패·취소분)
 * 작업 id 로는 어느 거래의 명세인지 말할 수 없다. `W-30` 의 정산 줄이 `settlement=jobId` 로
 * 데려오면 아래 `fetchJobBilling` 이 그 짝을 받아 온다. */
export function fetchRefundStatement(paymentId: string): Promise<RefundStatementView> {
  return request<RefundStatementView>(paymentEndpoints.statement(paymentId));
}

/** `GET /api/payments/by-job/:jobId`. 그 작업의 살아 있는 결제 id.
 *
 * ⚠️ **목록에서 찾던 것을 걷었다**(2026-09-14 이중 코드리뷰). 청구 내역 응답은 500줄에서
 * 잘리므로 **줄이 그 수를 넘은 셀러는 오래된 작업의 명세를 영영 열지 못하고**, 화면은
 * "그 작업의 청구 기록을 찾지 못했습니다"라고 거짓말했다. 상한을 쪽 나누기 대신으로 정한
 * 결정과 그 조회가 서로를 모르고 있었다. */
export function fetchJobBilling(jobId: string): Promise<JobBillingView> {
  return request<JobBillingView>(paymentEndpoints.forJob(jobId));
}

/** `GET /api/payments/checkout/:jobId`. **위젯을 띄우기 전에 서버에 금액을 물어본다.**
 *
 * ⛔ **여기서 받은 값으로 화면이 다시 계산하지 마라.** `amount` 를 그대로 위젯에 넣고,
 * 그대로 `confirmPayment` 에 돌려보낸다. 화면이 중간에 곱하면 서버의 대조가 **화면이 만든
 * 수와 화면이 만든 수**를 비교하는 일이 된다.
 *
 * ⛔ **`clientKey` 를 소스에 박지 마라.** 이 응답이 들고 온다. 박으면 테스트 키와 라이브 키를
 * 바꾸는 일이 프런트엔드 재배포가 되고, 그 사이에 서버와 위젯이 다른 키를 쓰는 상태가 생긴다.
 * 그 상태의 증상은 승인 단계의 401 하나뿐이라 원인이 안 보인다. */
export function fetchPaymentCheckout(jobId: string): Promise<PaymentCheckoutView> {
  return request<PaymentCheckoutView>(paymentEndpoints.checkout(jobId));
}

/** `POST /api/payments/confirm`. **위젯이 돌려준 셋을 그대로 서버에 넘긴다.**
 *
 * ⚠️ **실패를 한 낱말로 뭉개지 마라.** `code` 가 셋을 가른다.
 *   - `PAYMENT_DECLINED`(402) · `PAYMENT_AMOUNT_MISMATCH`(409) : **돈이 움직이지 않았다.**
 *   - `PAYMENT_ALREADY_PAID`(409) : **이미 결제됐다.** 작업을 다시 읽으면 된다.
 *   - `PAYMENT_GATEWAY_FAILED`(502) : ⛔ **움직였는지 모른다.** "실패했습니다"라고 단정하면
 *     안 되고, 같은 값으로 다시 부르는 것은 안전하다(서버가 같은 `paymentKey` 로 이어서
 *     복구한다).
 *
 * ⛔ **화면이 자동으로 되풀이 호출하는 자리를 만들지 마라.** 승인 호출은 돈이 움직이는
 * 자리다. 다시 부르는 것은 셀러가 누를 때다.
 *
 * **예외는 하나이고 상한이 있다**(2026-10-04): 서버가 409 `PAYMENT_IN_PROGRESS` 를 주면 웹
 * 결제 화면이 **같은 요청(같은 `paymentKey`)** 을 정해 둔 간격으로 몇 번 더 보낸다
 * (`screens/stages/Pay.tsx` 의 `confirmWithBusyRetry` · `CONFIRM_BUSY_RETRY_DELAYS_MS`, 최대
 * 5번 · 약 15초). 그 409 는 서버가 작업 처리 잠금을 못 쥐어 **PG 승인을 부르지 않은** 갈래이고,
 * 같은 키는 서버가 저장된 결과로 받아 주므로 돈이 두 번 움직이지 않는다. 그 밖의 실패는
 * 자동으로 다시 부르지 않는다. ⛔ 이 예외를 다른 코드로 넓히지 마라. */
export function confirmPayment(body: ConfirmPaymentRequest): Promise<ConfirmPaymentResult> {
  return request<ConfirmPaymentResult>(paymentEndpoints.confirm(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** `POST /api/payments/client-error`. 결제위젯이 브라우저에서 실패한 **단계와 코드만** 서버 로그로 보낸다.
 *
 * ⭐ **한 번 쏘고 잊는다.** 응답을 보지 않고, 실패해도 삼키고, 다시 보내지 않는다. 이 보고가 실패해도
 * 셀러가 겪는 것은 같다. 결제 화면의 흐름을 이 호출이 막으면 안 된다.
 * ⛔ **`request()` 를 쓰지 않는다.** 그쪽은 401 에 로그아웃 처리를 건다. 진단 보고 하나가 셀러를
 * 로그인 화면으로 보내면 안 된다.
 * ⛔ **세 칸 밖을 싣지 않는다**(토스 `message` · 키 · URL. `docs/ERROR_LOGGING.md` §3-4-5 (바)). */
export function reportPaymentClientError(body: PaymentClientErrorRequest): void {
  const payload: PaymentClientErrorRequest = { jobId: body.jobId, stage: body.stage, code: body.code };
  try {
    void fetch(`${getBaseUrl()}${paymentEndpoints.clientError()}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  } catch {
    // 보고는 진단용이다. 무엇이 터져도 화면으로 올리지 않는다.
  }
}

/** 장당 과금이다. 화면이 자동으로 반복 호출하는 자리를 만들지 않는다. */
export function processJob(jobId: string): Promise<JobResponse> {
  return request<JobResponse>(jobEndpoints.process(jobId), { method: "POST" });
}

/** `POST /api/jobs/:jobId/images/:imageId/process`. **`W-21` 의 못 만든 장 `다시 만들기`**
 *  (앱 `A-25b` 와 같은 일. 앱 `apps/mobile/src/lib/api.ts` 의 같은 이름 함수).
 *
 * ⚠️ **장당 과금이다.** 한 번 부르면 공급자 호출 한 번이고 우리 원가가 나간다. 셀러에게는
 * 더 내실 금액이 없다(그 장 금액을 그대로 쓴다. 2026-09-23 확정). 그래서 규칙이 둘이다.
 *
 * ① **자동으로 다시 걸지 않는다.** `request()` 에 재시도가 없고 여기에도 두지 않는다.
 *    다시 부르는 것은 셀러가 누를 때다. 이중 실행은 서버의 작업 잠금이 409 로 끊는다.
 * ② **본문이 없다.** 결과가 없는 장이라 덮어쓸 것이 없다(앱 쪽 주석과 같은 근거).
 *
 * ⛔ **만든 장 다시 만들기(`jobEndpoints.remakeImage`)를 이 파일에 붙이지 마라.** 그 라우트는
 * 장당 ₩500 을 새로 청구하는데 지금 서버가 새 결제를 받지 않고 청구 항목만 붙여, 승인한
 * 금액을 넘는 미청구가 생긴다(`docs/DATA_DICTIONARY.md` M-14 미반영). 웹은 그 갈래를 꺼 둔다.
 * ⛔ **두 주소를 합치지 마라.** 낱말이 같아도 라우트가 갈린다(`packages/shared/src/api.ts`). */
export function reviveImage(jobId: string, imageId: string): Promise<JobResponse> {
  return request<JobResponse>(jobEndpoints.retryImage(jobId, imageId), { method: "POST" });
}

export function getJob(jobId: string): Promise<JobResponse> {
  return request<JobResponse>(jobEndpoints.get(jobId));
}

/** `A-12`·`W-50` 타임라인. `W-31` 은 이 목록에서 **끝난 시각 한 줄**만 뽑아 쓴다.
 *
 * ⚠️ 그 화면이 `DetailJobView.updatedAt` 을 쓰지 않는 이유가 여기 있다 — 상품명을 고치거나
 * 판을 확정하면 그 칸이 밀려 올라가 **"끝난 시각"이 조용히 편집 시각이 된다.** */
export function fetchJobEvents(jobId: string): Promise<JobEventListView> {
  return request<JobEventListView>(jobEndpoints.events(jobId));
}

// ── 허용 편집 넷 ─────────────────────────────────────────────────────────────
//
// **저장 버튼이 없는 화면이다**(`W-30`). 값이 바뀔 때마다 부르므로 이 셋은 전부 조용히
// 성공하거나 조용히 실패한다 — 그래서 **부르는 쪽이 실패를 반드시 화면에 띄워야 한다.**
// 실패를 삼키면 셀러는 `저장됨` 을 보고 창을 닫고, 다음에 열었을 때 값이 돌아와 있다.

/** 상품명·배경(`PATCH`). **바뀐 칸만 보낸다** — 두 칸을 늘 함께 보내면 다른 기기에서 방금
 *  바꾼 값을 덮는다(`jobEndpoints.update` 주석). */
export function updateJob(jobId: string, edits: UpdateJobRequest, onlyIfNameEmpty = false): Promise<JobResponse> {
  return request<JobResponse>(`${jobEndpoints.update(jobId)}${onlyIfNameEmpty ? "?onlyIfNameEmpty=1" : ""}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(edits),
  });
}

/** `W-26` 오른쪽 패널의 `상품 종류`. **셀러가 자기 상품을 두고 하는 진술을 기록한다**
 *  (`A-26c` 와 같은 시점 · 같은 문장. PRD `7.6.9b`).
 *
 * ⭐ **위 편집 셋과 성격이 다르다.** 그쪽은 **값을 덮는 것**이라 실패를 삼키면 값이
 * 돌아오지만, 이쪽은 **행위를 적는 것**이라 실패를 삼키면 **하지도 않은 일을 했다고 화면이
 * 말한다.** 부르는 쪽이 실패를 반드시 띄워야 하는 것은 같고, 이유가 더 무겁다.
 *
 * ⛔ **결제를 막는 데 쓰지 마라.** 진술이 없어도 결제·배경 지우기·내보내기는 그대로다. */
export function stateProductCategory(
  jobId: string,
  statement: ProductCategoryStatement,
): Promise<ProductCategoryStatementResponse> {
  const payload: ProductCategoryStatementRequest = { statement };
  return request<ProductCategoryStatementResponse>(
    jobEndpoints.productCategoryStatement(jobId),
    { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) },
  );
}

/** 생성 값은 저장하지 않는다. 확인 후 기존 updateJob으로 적용한다. */
export function generateProductName(jobId: string): Promise<{ productName: string }> {
  return request<{ productName: string }>(`/api/jobs/${encodeURIComponent(jobId)}/product-name-draft`, { method: "POST" });
}

/** 블록 켜고 끄기(`PUT`). **여러 개를 한 요청에 담는다** — 토글 하나마다 부르면 연속으로
 *  두 개를 끌 때 두 요청이 겹치고 뒤엣것이 앞엣것을 되살린다. */
export function updateJobBlocks(
  jobId: string,
  blocks: UpdateJobBlocksRequest["blocks"],
): Promise<JobResponse> {
  return request<JobResponse>(jobEndpoints.blocks(jobId), {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ blocks } satisfies UpdateJobBlocksRequest),
  });
}

/** 사진 순서(`PUT`). ⛔ **그 작업의 사진 전부를 빠짐없이·중복 없이 보낸다.** 부분 배열은
 *  서버가 `IMAGE_SET_MISMATCH` 로 거절한다 — 빠진 장이 옛 순번을 쥔 채 남아 두 장이 같은
 *  자리를 주장하기 때문이다. */
export function reorderJobImages(jobId: string, imageIds: string[]): Promise<JobResponse> {
  return request<JobResponse>(jobEndpoints.imageOrder(jobId), {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ imageIds } satisfies ReorderJobImagesRequest),
  });
}

// ── 판 ───────────────────────────────────────────────────────────────────────

/** 확인·확정한 장을 본문에 싣는다(PRD `13.1` `O-73`).
 *
 * ⛔ **안 주면 본문을 아예 안 보낸다.** 서버가 그것을 **「이 축을 아직 안 보내는
 * 클라이언트」** 로 읽어 오늘 경로로 간다. ⛔ **빈 배열을 지어내 보내지 마라** — 그것은
 * "하나도 안 골랐다"는 뜻이라 409 로 막힌다. */
function publishSelectionBody(confirmedImageIds?: readonly string[]): RequestInit {
  if (confirmedImageIds === undefined) return {};
  return {
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      confirmedImageIds: [...confirmedImageIds],
    } satisfies PublishSelectionRequest),
  };
}

/** `W-31` 의 `좋아요 · 이 판으로 확정`. **판을 만든다**(PRD 7.9 의 앞쪽 지점).
 *
 * ⚠️ 응답의 `created === false` 는 **실패가 아니다.** 마지막 판과 상태가 같아 그 판을 그대로
 * 가리킨 것이고(두 번 누르기·응답 유실 재시도), `version` 은 채워져 있다.
 *
 * ⭐ `confirmedImageIds` 는 **셀러가 이번 판에 넣기로 확정한 장**이고, **그 장만 청구된다**
 * (`O-73`). 화면이 그 집합을 어떻게 받는지는 아직 규격이 없어 지금은 아무도 안 넘긴다. */
export function approveJob(
  jobId: string,
  confirmedImageIds?: readonly string[],
): Promise<ApproveJobResponse> {
  return request<ApproveJobResponse>(jobEndpoints.approve(jobId), {
    method: "POST",
    ...publishSelectionBody(confirmedImageIds),
  });
}

/** `W-40` 의 내보내기. 판을 만들고 작업을 `내보냄` 으로 올린다(PRD 7.9 의 뒤쪽 지점).
 *  `created` 와 `confirmedImageIds` 의 뜻은 위 `approveJob` 과 같다. */
export function exportJob(
  jobId: string,
  confirmedImageIds?: readonly string[],
): Promise<ExportJobResponse> {
  return request<ExportJobResponse>(jobEndpoints.export(jobId), {
    method: "POST",
    ...publishSelectionBody(confirmedImageIds),
  });
}

/** 그 작업의 판 전부. **파일 목록까지 함께 온다** — `W-40` 이 이름·크기·용량을 한 번에
 *  그려야 해서 서버가 한 응답에 담는다. */
export function fetchJobVersions(jobId: string): Promise<JobVersionListView> {
  return request<JobVersionListView>(jobEndpoints.versions(jobId));
}

/** 판 하나를 묶어 받는 주소. **`fetch` 가 아니라 최상위 이동이다** — 브라우저가 파일로
 *  받아야 하므로 `<a href>` 로 건다. `fetch` 로 부르면 바이트가 메모리에 올라올 뿐
 *  셀러 손에는 아무것도 안 남는다(`loginStartUrl` 과 같은 성격의 자리다). */
export function versionBundleUrl(jobId: string, versionId: string): string {
  return `${getBaseUrl()}${jobEndpoints.versionBundle(jobId, versionId)}`;
}

/** 결과물은 R2 가 아니라 API 를 거쳐 내려온다. */
export function absoluteUrl(path: string | null): string | null {
  return path ? `${getBaseUrl()}${path}` : null;
}

// ── 로그인 ───────────────────────────────────────────────────────────────────
//
// **경로와 낱말이 2026-09-09 에 shared 로 올라갔다.** 이 자리에는 그동안 `authEndpoints` ·
// `IDENTITY_PROVIDER_LABEL` · `LOGIN_REASON_PARAM` 사본이 있었고, 그 주석이 **"여기 두는
// 동안 앱이 붙으면 같은 문자열이 한 번 더 적히고, 두 벌이 갈리는 순간 한쪽이 없는 주소를
// 부른다"** 고 경고해 뒀다. **앱이 붙는 회차가 이번이라 그 경고대로 걷었다.**
// 셋 다 `@makit/shared` 에서 온다. 남은 것은 화면 함수(`accountLabel`)와 URL 조립뿐이다.

/** 계정 메뉴(`W-20`)와 마이페이지 머리(`W-70`)에 적는 한 줄.
 *
 * **빈 조각을 구분자로 잇지 않는다.** 이름을 못 받은 계정에서 `· 네이버` 처럼 앞이 비어
 * 렌더되던 자리다. Apple 이 붙으면 이름 없는 계정이 흔해져 그대로 드러난다. 두 화면이
 * 같은 함수를 쓰는 이유는 **머리와 본문이 다른 사람을 가리키면 그 화면을 못 믿기** 때문이다. */
export function accountLabel(session: SessionView): string {
  const provider = session.provider ? IDENTITY_PROVIDER_LABEL[session.provider] : null;
  return [session.displayName, provider].filter((v): v is string => Boolean(v)).join(" · ");
}

// ── 401 을 봤을 때 ───────────────────────────────────────────────────────────
//
// ⚠️ **401 은 "이 요청이 실패했다"가 아니라 "로그인이 끝났다"는 사실이다.** 이 자리가
// 없으면 세션 캐시는 `authenticated: true` 로 남고, 셀러는 로그인된 화면에서 모든 동작만
// 조용히 실패하는 상태를 본다. 세션 만료와 다른 기기에서의 전체 로그아웃이 그 경로다.
//
// 게이트(`SessionGate`)가 이 함수에 자기 무효화를 걸어 둔다. **여기서 화면을 옮기지
// 않는다.** "로그인했는가"를 판단하는 자리는 하나여야 하고 그 자리가 게이트다.

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function notifyUnauthorized(status: number, path: string): void {
  if (status !== 401) return;
  // 세션 질문 자체는 미인증도 200 이라 여기 오지 않는다. 그래도 걸러 둔다. 오면
  // 무효화가 다시 그 질문을 부르는 고리가 된다.
  if (path === authEndpoints.session()) return;
  onUnauthorized?.();
}

/** 인가 시작 주소. **fetch 가 아니라 최상위 이동이다** — 공급자 화면으로 나갔다가
 *  돌아와야 하므로 `<a href>` 로 건다.
 *
 * 인자가 `IdentityProvider` 다. 예전에는 소문자 리터럴(`"naver"`)을 받았는데, 주소의 소문자
 * 변환은 shared 의 `authEndpoints.start` 가 이미 한다. **낱말을 두 서식으로 들고 다니지
 * 않는다** — 화면은 `ENABLED_IDENTITY_PROVIDERS` 에서 값을 꺼내 그대로 넘기면 되고, 그러면
 * Apple 이 켜질 때 이 화면을 고치지 않아도 된다.
 *
 * ⚠️ **`source` 를 붙이지 않는다.** 없으면 서버가 `WEB` 으로 본다. 앱만 `source=APP` 과
 * PKCE 를 싣는다(`apps/mobile/src/lib/api.ts` 의 같은 이름 함수). 두 함수가 이름은 같고
 * 인자가 다른 것이 맞다 — 웹은 어디로 돌아갈지를 보내고, 앱은 무엇으로 묶을지를 보낸다. */
export function loginStartUrl(provider: IdentityProvider, returnTo?: string): string {
  const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
  return `${getBaseUrl()}${authEndpoints.start(provider)}${query}`;
}

/** 켤 때 한 번 묻는 "나 누구인가". **미인증도 200 이다.** 401 이 와도 미로그인으로 읽는다.
 *
 * ⛔ **그 밖의 실패(네트워크 끊김 · 5xx · 깨진 본문)는 미로그인이 아니다. 던진다.** 예전에는
 * 모든 실패를 `ANONYMOUS_SESSION` 으로 바꿨다. 세션은 탭으로 돌아올 때마다 다시 묻는데
 * (`SessionGate`), 그러면 지하철에서 한 번 끊기거나 API 가 잠깐 500 을 내기만 해도 **로그인한
 * 셀러가 로그인 화면으로 튕기고**, 게이트가 데이터를 버려 올리던 화면이 처음부터 다시 선다.
 * 던지면 세션 질의가 오류 상태가 되고 react-query 는 **앞 답을 그대로 들고 있다** — 같은 셀러가
 * 같은 화면에 남는다. 처음 켤 때부터 실패하면 들고 있는 답이 없어 게이트가 미로그인으로 그린다
 * (`query.data ?? ANONYMOUS_SESSION`). API 가 통째로 죽었을 때 흰 화면보다 로그인 화면이 낫다는
 * 옛 판단은 그 자리에 그대로 남는다. */
export async function fetchSession(): Promise<SessionView> {
  try {
    return await request<SessionView>(authEndpoints.session());
  } catch (error) {
    if (sessionFailureMeansSignedOut(error)) return ANONYMOUS_SESSION;
    throw error;
  }
}

/** 세션 질문의 실패 중 **미로그인으로 읽을 것.** 401 하나뿐이다. */
export function sessionFailureMeansSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** `W-70` 의 `내 계정` 두 줄. **이름과 이메일뿐이다.**
 *
 * ⚠️ 정본 `@makit/shared` 의 `AccountProfile` 에는 `joinedAt`(서버 가입 시각)이 더 있다. 응답에는 실려 오지만
 * W-70 이 그리지 않아 이 사본은 일부러 뺐다. 웹이 가입일을 쓰게 되면 이 사본을 지우고 정본을 쓴다. */
export interface AccountProfile {
  displayName: string | null;
  email: string | null;
}

export function fetchAccount(): Promise<AccountProfile> {
  return request<AccountProfile>(authEndpoints.account());
}

/** 로그아웃. **이 계정의 세션 전부가 끊긴다** — 앱에서 로그아웃하면 웹도 끊긴다고
 *  화면에 적기로 했고, 한쪽만 끊으면 그 문장이 거짓말이 된다(PRD `7.6.23`). */
export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(authEndpoints.logout(), { method: "POST" });
}

// ── 아이디로 로그인 (`W-01b` · `W-01c`) ─────────────────────────────────────────
//
// 결제사 심사자 한 사람이 쓰는 길이다(PRD `7.0` 「로그인 전 화면」). 셀러 로그인은 네이버 ·
// 카카오 그대로이고, 이 길로는 가입 · 비밀번호 찾기가 없다.
//
// 경로는 shared 의 `authEndpoints.reviewLogin()` 한 벌이다. 서버 라우트도 같은 줄을 읽는다.

/** 아이디 로그인의 결과. **화면이 고를 문장의 갈래만 담는다.** 서버 메시지는 싣지 않는다.
 *
 * - `ok`: 세션 쿠키가 섰다. 로그인했는지는 여기서 단정하지 않고 세션 질문이 다시 확인한다.
 * - `mismatch`(401 `REVIEW_LOGIN_FAILED`): 아이디나 비밀번호가 맞지 않는다. **어느 칸인지
 *   가르지 않는다.** 가르면 어느 아이디가 있는지를 밖에서 알아낼 수 있다(`W-01c`).
 * - `locked`(429 `REVIEW_LOGIN_LOCKED`): 여러 번 맞지 않아 잠시 막혔다.
 * - `off`(404): 이 로그인 방법이 지금 꺼져 있다.
 * - `error`: 그 밖. 네트워크가 끊긴 경우도 여기다. */
export type ReviewLoginResult = "ok" | "mismatch" | "locked" | "off" | "error";

/** `POST /api/auth/review/login`.
 *
 * `request()` 를 거치지 않는다. 그 길은 401 을 「세션이 만료됐다」로 읽어 세션 캐시를
 * 무효화하는데(`notifyUnauthorized`), 여기 401 은 비밀번호가 틀렸다는 뜻이다. 쿠키 규약
 * (`credentials: "include"`)과 주소 규약(`getBaseUrl()`)은 같다.
 *
 * 비밀번호는 본문에만 싣고 어디에도 남기지 않는다. 화면은 실패하면 그 칸을 비운다. */
export async function reviewLogin(loginId: string, password: string): Promise<ReviewLoginResult> {
  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}${authEndpoints.reviewLogin()}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ loginId, password }),
    });
  } catch {
    return "error";
  }
  if (res.ok) return "ok";
  if (res.status === 404) return "off";
  const code = await readErrorCode(res);
  if (res.status === 401 && code === "REVIEW_LOGIN_FAILED") return "mismatch";
  if (res.status === 429 && code === "REVIEW_LOGIN_LOCKED") return "locked";
  return "error";
}
